import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { useSuggestionsForField } from '../../store/suggestions';
import type { Field, Suggestion } from '../../store/types';
import { schemaToFieldType } from '../../store/wire';
import InlineEditPair from './primitives/inline-edit-pair';
import PatchCard from './primitives/patch-card';
import ProseDiff from './primitives/prose-diff';
import { diffField } from './utils/diff-field';
import { pickNoteKey } from './utils/note-key';
type Props = {
  taskId: string;
  field: Field;
};
const FieldSuggestions = t0 => {
  const $ = _c(7);
  const {
    taskId,
    field
  } = t0;
  const suggestions = useSuggestionsForField(taskId, field.id);
  if (suggestions.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== field || $[1] !== suggestions) {
    let t2;
    if ($[3] !== field) {
      t2 = s => <FieldPatchCard key={s.id} suggestion={s} field={field} />;
      $[3] = field;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = suggestions.map(t2);
    $[0] = field;
    $[1] = suggestions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex flex-col gap-2 mt-2">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const FieldPatchCard = t0 => {
  const $ = _c(42);
  const {
    suggestion,
    field
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.suggestion.field"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.canvas.suggestion.labels"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tLabels
  } = useTranslation("agents", t2);
  const op = suggestion.operation;
  const isStale = !suggestion.canApply;
  if (op.kind === "remove_task_field") {
    const t3 = suggestion.id;
    let t4;
    if ($[2] !== t) {
      t4 = t(_temp);
      $[2] = t;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== field.name || $[5] !== t) {
      t5 = t(_temp2, {
        name: field.name
      });
      $[4] = field.name;
      $[5] = t;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== t5) {
      t6 = <p className="text-caption-3 text-tertiary">{t5}</p>;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== isStale || $[10] !== suggestion.id || $[11] !== t4 || $[12] !== t6) {
      t7 = <PatchCard suggestionId={t3} note={t4} isStale={isStale}>{t6}</PatchCard>;
      $[9] = isStale;
      $[10] = suggestion.id;
      $[11] = t4;
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  if (op.kind === "update_task_field") {
    let t3;
    if ($[14] !== field.prompt || $[15] !== op.prompt) {
      t3 = diffField(field.prompt, op.prompt);
      $[14] = field.prompt;
      $[15] = op.prompt;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    const promptChange = t3;
    let t4;
    if ($[17] !== field.type || $[18] !== op.schema) {
      const suggestedType = op.schema ? schemaToFieldType(op.schema) : undefined;
      t4 = diffField(field.type, suggestedType);
      $[17] = field.type;
      $[18] = op.schema;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    const schemaChange = t4;
    const t5 = !!promptChange;
    const t6 = !!schemaChange;
    let t7;
    if ($[20] !== t5 || $[21] !== t6) {
      t7 = pickNoteKey({
        refinedPrompt: t5,
        refinedSchema: t6
      }, "refinedPromptAndSchema" as const);
      $[20] = t5;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    const noteKey = t7;
    if (!noteKey) {
      return null;
    }
    const t8 = suggestion.id;
    let t9;
    if ($[23] !== noteKey) {
      t9 = $_1 => $_1[noteKey];
      $[23] = noteKey;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t || $[26] !== t9) {
      t10 = t(t9);
      $[25] = t;
      $[26] = t9;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== schemaChange || $[29] !== tLabels) {
      t11 = schemaChange && <InlineEditPair label={tLabels(_temp3)} current={schemaChange.from} suggested={schemaChange.to} />;
      $[28] = schemaChange;
      $[29] = tLabels;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    let t12;
    if ($[31] !== promptChange || $[32] !== tLabels) {
      t12 = promptChange && <div className="flex flex-col gap-0.5"><span className="text-caption-3 text-tertiary">{tLabels(_temp4)}:</span><ProseDiff current={promptChange.from} suggested={promptChange.to} /></div>;
      $[31] = promptChange;
      $[32] = tLabels;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    let t13;
    if ($[34] !== t11 || $[35] !== t12) {
      t13 = <div className="flex flex-col gap-2">{t11}{t12}</div>;
      $[34] = t11;
      $[35] = t12;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    let t14;
    if ($[37] !== isStale || $[38] !== suggestion.id || $[39] !== t10 || $[40] !== t13) {
      t14 = <PatchCard suggestionId={t8} note={t10} isStale={isStale} showDiffToggle={true}>{t13}</PatchCard>;
      $[37] = isStale;
      $[38] = suggestion.id;
      $[39] = t10;
      $[40] = t13;
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    return t14;
  }
  return null;
};
export default FieldSuggestions;
function _temp($) {
  return $.remove;
}
function _temp2($_0) {
  return $_0.removeBody;
}
function _temp3($_2) {
  return $_2.type;
}
function _temp4($_3) {
  return $_3.prompt;
}